import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useExperimentSearchParams, useExperimentsTranslations } from 'modules/experiments/hooks';
import {
  defaultTimePreset,
  RELATIVE_DATE_PRESETS_WITH_LABELS,
  STATE_WITH_EMPTY_DETAILS_TAB,
} from 'modules/experiments/constants';
import { Presets, TimePreset } from 'modules/experiments/types';
import {
  getCampaignType,
  getExperimentLoadingState,
  getExperimentStateStatus,
  getExperimentTimeFilterMetadata,
} from 'modules/experiments/ducks/selectors';
import { getFinishedExperimentStates } from 'modules/experiments/utils';
import { useClickAway } from 'react-use';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { BUTTON_SIZES } from 'ui/button';
import { Calendar16 } from 'ui/icons';
import { DATE_FORMAT_TRUNC_YEAR, displayDate, getNewDate } from 'utils/date';
import { DateRangePicker } from 'ui/datePicker';
import { RAW_SELECT_DATA_PORTAL_ID } from 'ui/selects';
import { colors } from 'ui/theme';

import {
  ButtonText,
  DatePickerContainer,
  DatePickerTriggerButton,
  DatePickerWrapper,
  StyledChevronDown,
} from './Styled';

export const ExperimentDateTimePicker = () => {
  const { t, ct } = useExperimentsTranslations();
  const loading = useSelector(getExperimentLoadingState);
  const experimentState = useSelector(getExperimentStateStatus);
  const campaignType = useSelector(getCampaignType);
  const { experimentStartDate, experimentEndDate } = useSelector(getExperimentTimeFilterMetadata);
  const {
    startDateTime: urlStartDate,
    endDateTime: urlEndDate,
    timezone,
    setDateParams,
    setTimezone,
  } = useExperimentSearchParams();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [timePreset, setTimePreset] = useState(
    /**
     * If, the url has a startTime we just default to a custom time range
     * Instead of computing if it falls into a particular time preset.
     */
    urlStartDate === null ? defaultTimePreset : TimePreset.CUSTOM_RANGE,
  );
  const datePickerRef = useRef(null);

  useClickAway(datePickerRef, () => {
    const timeZonePickerDropdownPortal = document.querySelector(
      `[data-portal-id="${RAW_SELECT_DATA_PORTAL_ID}"]`,
    ); // NOTE: only one of these should exist at a time

    /**
     * If the click originated from the time zone dropdown(i.e. the dropdown is open),
     * don't close the date picker. The dropdown portal must be close before we start acknowledging
     * clicks outside of the date picker.
     */
    if (timeZonePickerDropdownPortal) {
      return;
    }
    setShowDatePicker(false);
  });

  // Time ranges for the custom experiment preset and valid date ranges
  const experimentTimePresetStartDate = getNewDate({
    date: experimentStartDate,
    timeZone: timezone,
  });
  const experimentTimePresetEndDate = getNewDate({
    date: experimentEndDate || new Date(),
    timeZone: timezone,
  });
  const metricStartDate = urlStartDate
    ? getNewDate({
        date: urlStartDate,
        timeZone: timezone,
      })
    : null;
  const metricEndDate = urlEndDate
    ? getNewDate({
        date: urlEndDate,
        timeZone: timezone,
      })
    : null;
  const today = getNewDate({ date: new Date(), timeZone: timezone });
  const startDate = metricStartDate || experimentTimePresetStartDate;
  const endDate = metricEndDate || experimentTimePresetEndDate;
  const isFinished = getFinishedExperimentStates(campaignType).includes(experimentState);
  const isDisableDatePicker =
    loading || !experimentStartDate || STATE_WITH_EMPTY_DETAILS_TAB.includes(experimentState);

  // Adding filters for the experiment specific time presets
  const RELATIVE_TIME_PRESETS_WITH_EXPERIMENT_LENGTH: Presets = {
    ...RELATIVE_DATE_PRESETS_WITH_LABELS,
    [TimePreset.EXPERIMENT_PERIOD]: {
      menuLabel: 'timePresets.menu.experimentPeriod',
      filterLabel: 'timePresets.menu.experimentPeriod',
      format: () => ({
        startDate: experimentTimePresetStartDate,
        endDate: experimentTimePresetEndDate,
      }),
    },
    ...(isFinished
      ? {
          [TimePreset.EXPERIMENT_START_TO_NOW]: {
            menuLabel: 'timePresets.menu.experimentStartToNow',
            filterLabel: 'timePresets.filter.experimentStartToNow',
            format: () => ({
              startDate: experimentTimePresetStartDate,
              endDate: today,
            }),
          },
        }
      : {}),
  };

  const datePickerButtonText = ct(
    RELATIVE_TIME_PRESETS_WITH_EXPERIMENT_LENGTH[timePreset]?.filterLabel || '',
    {
      startDate: displayDate(startDate, DATE_FORMAT_TRUNC_YEAR),
      endDate: displayDate(endDate, DATE_FORMAT_TRUNC_YEAR),
    },
  );

  return (
    <>
      <span>{ct('labels.timeRangeHeader')}</span>
      <DatePickerContainer isDisabled={isDisableDatePicker}>
        <Tooltip
          isEnabled={isDisableDatePicker}
          tooltipContent={t('timeFilter.no_data')}
          placement={TipPlacement.Right}
        >
          <DatePickerTriggerButton
            isDisabled={isDisableDatePicker}
            size={BUTTON_SIZES.LARGE}
            onClick={() => setShowDatePicker((state) => !state)}
            data-test="experiments-monitor-time-range-trigger-button"
          >
            <Calendar16 />
            <ButtonText isDisabled={isDisableDatePicker}>{datePickerButtonText}</ButtonText>
            <StyledChevronDown height={18} color={colors.grey50} />
          </DatePickerTriggerButton>
        </Tooltip>
        {showDatePicker && (
          <DatePickerWrapper ref={datePickerRef} data-test="experiments-monitor-time-range-picker">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              validDateRange={{ start: experimentTimePresetStartDate, end: today }}
              onDateRangeChange={(newCustomDateRange) => {
                setDateParams(newCustomDateRange);
              }}
              useLocalTimeZone={false}
              defaultTimeZone={timezone}
              onTimeZoneChange={(tz) => setTimezone(tz)}
              dateRangePreset={timePreset}
              onPresetChange={(preset) => setTimePreset(preset as TimePreset)}
              presets={RELATIVE_TIME_PRESETS_WITH_EXPERIMENT_LENGTH}
              promoteCustomInput
              blockFutureDates
              showRelativeDatePresets
            />
          </DatePickerWrapper>
        )}
      </DatePickerContainer>
    </>
  );
};
